import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  BarChart3,
  TrendingUp,
  Users,
  Clock,
  AlertTriangle,
  CheckCircle,
  Calendar,
  Download,
  Filter
} from 'lucide-react';

import { apiHelper } from '@services/api';
import Button from '@components/common/Button';
import Loading, { CardSkeleton } from '@components/common/Loading';

const Analytics = () => {
  const [timeRange, setTimeRange] = useState('7d');

  // Fetch analytics data
  const {
    data: analyticsData,
    isLoading,
    error
  } = useQuery(
    ['analytics', timeRange],
    async () => {
      // For now, we'll simulate analytics data since the API isn't provided
      // Replace with actual API call when available
      return {
        overview: {
          totalTickets: 256,
          openTickets: 42,
          closedTickets: 214,
          avgResolutionTime: '2.3 hours',
          customerSatisfaction: 4.2
        },
        trends: {
          ticketsCreated: [12, 19, 8, 15, 22, 18, 25],
          ticketsResolved: [8, 15, 12, 18, 20, 16, 23]
        },
        priorities: {
          critical: 8,
          high: 15,
          medium: 12,
          low: 7
        },
        modules: [
          { name: 'Finance', tickets: 45, percentage: 18 },
          { name: 'Operations', tickets: 62, percentage: 24 },
          { name: 'Customer Service', tickets: 38, percentage: 15 },
          { name: 'IT Support', tickets: 51, percentage: 20 },
          { name: 'HR', tickets: 28, percentage: 11 },
          { name: 'Others', tickets: 32, percentage: 12 }
        ],
        performance: {
          avgFirstResponse: '1.2 hours',
          avgResolution: '2.3 hours',
          escalationRate: '8%',
          reopenRate: '3%'
        }
      };
    },
    {
      staleTime: 300000 // 5 minutes
    }
  );

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.06 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32 } }
  };

  const styles = {
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '1.5rem'
    },
    card: {
      background: '#fff',
      borderRadius: '1rem',
      padding: '1.5rem',
      boxShadow: '0 8px 24px rgba(2,6,23,0.04)',
      border: '1px solid rgba(14,20,25,0.04)'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem'
    },
    title: {
      fontSize: '1.125rem',
      fontWeight: 700,
      color: 'var(--gray-900)',
      margin: 0
    },
    statGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '1rem'
    },
    statCard: {
      padding: '1rem',
      borderRadius: '0.75rem',
      backgroundColor: 'var(--gray-50)',
      border: '1px solid rgba(14,20,25,0.04)'
    },
    statValue: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: 'var(--gray-900)',
      margin: 0
    },
    statLabel: {
      fontSize: '0.875rem',
      color: 'var(--gray-600)',
      marginTop: '0.25rem'
    },
    chartPlaceholder: {
      height: '200px',
      background: 'var(--gray-50)',
      borderRadius: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'var(--gray-500)',
      fontSize: '0.875rem'
    },
    moduleItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '0.75rem 0',
      borderBottom: '1px solid rgba(14,20,25,0.04)'
    },
    progressBar: {
      width: '100px',
      height: '6px',
      backgroundColor: 'var(--gray-200)',
      borderRadius: '3px',
      overflow: 'hidden'
    },
    progressFill: (percentage) => ({
      width: `${percentage}%`,
      height: '100%',
      backgroundColor: 'var(--primary-600)',
      borderRadius: '3px'
    })
  };

  if (error) {
    return (
      <div style={styles.card}>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <AlertTriangle style={{ width: '3rem', height: '3rem', color: 'var(--error-500)', margin: '0 auto 1rem' }} />
          <h3 style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--gray-900)', marginBottom: '0.5rem' }}>
            Failed to load analytics
          </h3>
          <p style={{ color: 'var(--gray-600)' }}>
            There was an error loading the analytics data.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={styles.grid}>
        {Array.from({ length: 6 }).map((_, index) => (
          <CardSkeleton key={index} style={{ height: 300 }} />
        ))}
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}
    >
      {/* Controls */}
      <motion.div variants={itemVariants}>
        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {[
                { label: 'Last 7 days', value: '7d' },
                { label: 'Last 30 days', value: '30d' },
                { label: 'Last 90 days', value: '90d' }
              ].map((range) => (
                <Button
                  key={range.value}
                  variant={timeRange === range.value ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setTimeRange(range.value)}
                >
                  {range.label}
                </Button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button variant="outline" size="sm" leftIcon={<Filter style={{ width: 14, height: 14 }} />}>
                Filter
              </Button>
              <Button variant="outline" size="sm" leftIcon={<Download style={{ width: 14, height: 14 }} />}>
                Export
              </Button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Overview Stats */}
      <motion.div variants={itemVariants}>
        <div style={styles.card}>
          <h2 style={styles.title}>Overview</h2>
          <div style={styles.statGrid}>
            <div style={styles.statCard}>
              <p style={styles.statValue}>{analyticsData?.overview.totalTickets}</p>
              <p style={styles.statLabel}>Total Tickets</p>
            </div>
            <div style={styles.statCard}>
              <p style={styles.statValue}>{analyticsData?.overview.openTickets}</p>
              <p style={styles.statLabel}>Open Tickets</p>
            </div>
            <div style={styles.statCard}>
              <p style={styles.statValue}>{analyticsData?.overview.avgResolutionTime}</p>
              <p style={styles.statLabel}>Avg Resolution Time</p>
            </div>
            <div style={styles.statCard}>
              <p style={styles.statValue}>{analyticsData?.overview.customerSatisfaction}/5</p>
              <p style={styles.statLabel}>Customer Satisfaction</p>
            </div>
          </div>
        </div>
      </motion.div>

      <div style={styles.grid}>
        {/* Ticket Trends Chart */}
        <motion.div variants={itemVariants}>
          <div style={styles.card}>
            <div style={styles.header}>
              <h3 style={styles.title}>Ticket Trends</h3>
              <TrendingUp style={{ width: 20, height: 20, color: 'var(--primary-600)' }} />
            </div>
            <div style={styles.chartPlaceholder}>
              📈 Ticket creation and resolution trends chart would go here
            </div>
          </div>
        </motion.div>

        {/* Priority Distribution */}
        <motion.div variants={itemVariants}>
          <div style={styles.card}>
            <div style={styles.header}>
              <h3 style={styles.title}>Priority Distribution</h3>
              <AlertTriangle style={{ width: 20, height: 20, color: 'var(--orange-600)' }} />
            </div>
            <div style={styles.statGrid}>
              <div style={{ ...styles.statCard, backgroundColor: 'var(--red-50)' }}>
                <p style={styles.statValue}>{analyticsData?.priorities.critical}</p>
                <p style={styles.statLabel}>Critical</p>
              </div>
              <div style={{ ...styles.statCard, backgroundColor: 'var(--orange-50)' }}>
                <p style={styles.statValue}>{analyticsData?.priorities.high}</p>
                <p style={styles.statLabel}>High</p>
              </div>
              <div style={{ ...styles.statCard, backgroundColor: 'var(--yellow-50)' }}>
                <p style={styles.statValue}>{analyticsData?.priorities.medium}</p>
                <p style={styles.statLabel}>Medium</p>
              </div>
              <div style={{ ...styles.statCard, backgroundColor: 'var(--green-50)' }}>
                <p style={styles.statValue}>{analyticsData?.priorities.low}</p>
                <p style={styles.statLabel}>Low</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Module Performance */}
        <motion.div variants={itemVariants}>
          <div style={styles.card}>
            <div style={styles.header}>
              <h3 style={styles.title}>Tickets by Module</h3>
              <BarChart3 style={{ width: 20, height: 20, color: 'var(--primary-600)' }} />
            </div>
            <div>
              {analyticsData?.modules.map((module, index) => (
                <div key={index} style={styles.moduleItem}>
                  <div>
                    <div style={{ fontWeight: 600, color: 'var(--gray-900)' }}>{module.name}</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>{module.tickets} tickets</div>
                  </div>
                  <div style={styles.progressBar}>
                    <div style={styles.progressFill(module.percentage)} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Performance Metrics */}
        <motion.div variants={itemVariants}>
          <div style={styles.card}>
            <div style={styles.header}>
              <h3 style={styles.title}>Performance Metrics</h3>
              <Clock style={{ width: 20, height: 20, color: 'var(--primary-600)' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={styles.statCard}>
                <p style={styles.statValue}>{analyticsData?.performance.avgFirstResponse}</p>
                <p style={styles.statLabel}>Avg First Response</p>
              </div>
              <div style={styles.statCard}>
                <p style={styles.statValue}>{analyticsData?.performance.escalationRate}</p>
                <p style={styles.statLabel}>Escalation Rate</p>
              </div>
              <div style={styles.statCard}>
                <p style={styles.statValue}>{analyticsData?.performance.reopenRate}</p>
                <p style={styles.statLabel}>Reopen Rate</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Analytics;